import { NavLink, useLocation } from 'react-router-dom';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { icons } from '../components/ui/Icons';
import { PageHeader } from '../components/layout/PageHeader';
import { navigationConfig } from '../config/navigation';
import { filterNavigationByPermissions } from '../config/navigation';
import { useAuth } from '../context/AuthContext';

interface PlaceholderPageProps {
  section: string;
}

const sectionPaths: Record<string, string> = {
  Personal: '/personal',
  Patienten: '/patienten',
  Medizin: '/medizin',
  Organisation: '/organisation',
  Wissen: '/wissen',
  Kommunikation: '/kommunikation',
  'Sicherheit & Notfall': '/notfall',
  Betrieb: '/betrieb',
  Verwaltung: '/verwaltung',
  System: '/system',
};

export function PlaceholderPage({ section }: PlaceholderPageProps) {
  const { permissions } = useAuth();
  const location = useLocation();
  const sectionPath = sectionPaths[section] || '/';
  const sectionItem = navigationConfig.find(item => item.path === sectionPath);
  const childItems = sectionItem?.children
    ? filterNavigationByPermissions(sectionItem.children, permissions)
    : [];
  const currentSubsection = childItems.find(item => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
  const title = currentSubsection?.label || section;
  const SectionIcon = icons[sectionItem?.icon as keyof typeof icons] || icons.Folder;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={title}
        description="Dieser Bereich ist im Intranet vorgesehen und wird schrittweise mit Fachfunktionen erweitert."
        breadcrumbs={[
          { label: 'Startseite', href: '/' },
          { label: section, href: sectionPath },
          ...(currentSubsection ? [{ label: currentSubsection.label }] : []),
        ]}
      />

      <Alert variant="info" title="Bereich im Aufbau">
        <p>Die Navigation ist bereits verfügbar. Fachdaten und Arbeitsabläufe werden in diesem Bereich noch ergänzt.</p>
      </Alert>

      {childItems.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Bereichsübersicht</CardTitle>
            <CardDescription>Verfügbare und geplante Unterseiten</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {childItems.map(item => {
                const Icon = icons[item.icon as keyof typeof icons] || icons.FileText;
                const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                return (
                  <div
                    key={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-md border px-3 py-2.5 text-sm',
                      active
                        ? 'border-primary-300 bg-primary-50 text-primary-800 dark:border-primary-800 dark:bg-primary-950/30 dark:text-primary-200'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:border-primary-300 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:border-primary-800'
                    )}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0 text-neutral-500" aria-hidden="true" />
                    <NavLink to={item.path} className="min-w-0 flex-1 truncate font-medium hover:underline">
                      {item.label}
                    </NavLink>
                    <Badge variant={active ? 'primary' : 'gray'} size="sm">{active ? 'Aktiv' : 'Geplant'}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <EmptyState
              icon="Construction"
              title="Inhalt wird vorbereitet"
              description="Für diesen Bereich sind noch keine Unterseiten hinterlegt."
            />
          </CardContent>
        </Card>
      )}

      <Card variant="bordered">
        <CardContent className="pt-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                <SectionIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-h3 font-semibold text-neutral-900 dark:text-neutral-100">{section}</h3>
                <p className="text-body text-neutral-600 dark:text-neutral-300">Fachmodul des Klinikum-Ravenbruch-Intranets</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-sm text-neutral-600 dark:text-neutral-300">
              <span className="inline-flex items-center gap-1.5"><icons.Shield className="h-4 w-4" /> Session-basiert </span>
              <span className="inline-flex items-center gap-1.5"><icons.Database className="h-4 w-4" /> Bestehende Datenbank </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function cn(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}
